import { useEffect, useMemo, useState } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import AppLoader from "components/AppLoader";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import { formatDateForUser } from "utils/dateTime";

function Listings() {
  const [listings, setListings] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);
  const [statusTargetListing, setStatusTargetListing] = useState(null);
  const [openEditListing, setOpenEditListing] = useState(false);
  const [editListingId, setEditListingId] = useState(0);
  const [editListingTitle, setEditListingTitle] = useState("");
  const [editListingDescription, setEditListingDescription] = useState("");
  const [editListingPrice, setEditListingPrice] = useState("");
  const [editListingQuantity, setEditListingQuantity] = useState("");
  const [editExistingImageUrls, setEditExistingImageUrls] = useState([]);
  const [editNewImageDataUrls, setEditNewImageDataUrls] = useState([]);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const parseBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "active";
    }
    return false;
  };

  const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);
  const getListingId = (item) =>
    Number(
      firstDefined(
        item?.id,
        item?.Id,
        item?.listingId,
        item?.ListingId,
        item?.listingID,
        item?.ListingID
      )
    ) || 0;
  const getTitle = (item) =>
    String(
      firstDefined(
        item?.title,
        item?.Title,
        item?.listingTitle,
        item?.ListingTitle,
        item?.name,
        item?.Name
      ) ?? ""
    ).trim();
  const getDescription = (item) =>
    String(
      firstDefined(
        item?.description,
        item?.Description,
        item?.listingDescription,
        item?.ListingDescription
      ) ?? ""
    ).trim();
  const getPrice = (item) =>
    Number(firstDefined(item?.price, item?.Price, item?.listingPrice, item?.ListingPrice, 0)) || 0;
  const getQuantity = (item) =>
    Number(
      firstDefined(item?.quantity, item?.Quantity, item?.listingQuantity, item?.ListingQuantity, 0)
    ) || 0;
  const getCreatedDate = (item) =>
    firstDefined(
      item?.dateCreated,
      item?.DateCreated,
      item?.createdDate,
      item?.CreatedDate,
      item?.createdOn,
      item?.CreatedOn
    ) ?? "";
  const getCategoryName = (item) =>
    firstDefined(
      item?.categoryName,
      item?.CategoryName,
      item?.category?.categoryName,
      item?.category?.CategoryName,
      item?.Category?.categoryName,
      item?.Category?.CategoryName
    ) ??
    (firstDefined(item?.categoryId, item?.CategoryId)
      ? `Category #${firstDefined(item?.categoryId, item?.CategoryId)}`
      : "-");
  const getListingImages = (item) => {
    const imageItems = normalizeArray(
      firstDefined(item?.listingImages, item?.ListingImages, item?.images, item?.Images)
    );
    return imageItems
      .map((image) => firstDefined(image?.imageUrl, image?.ImageUrl, image))
      .filter((value) => typeof value === "string" && value.trim())
      .map((value) => value.trim());
  };
  const isActive = (item) =>
    parseBool(
      firstDefined(
        item?.isActive,
        item?.IsActive,
        item?.active,
        item?.Active,
        item?.status,
        item?.Status
      )
    );

  const getTitleLabel = (item) => {
    const title = getTitle(item);
    if (title) return title;
    const id = getListingId(item);
    return id ? `Listing #${id}` : "Untitled Listing";
  };

  const getCategoryLabel = (item) =>
    item?.category?.categoryName ??
    item?.category?.CategoryName ??
    item?.Category?.categoryName ??
    item?.Category?.CategoryName ??
    getCategoryName(item);

  const getRowKey = (item, index) => {
    const id = getListingId(item);
    return id ? `id-${id}` : `idx-${index}`;
  };

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const handleToggleListingStatus = async (listing) => {
    const listingId = getListingId(listing);
    if (!listingId) {
      setError("Listing id is missing.");
      return;
    }

    const nextIsActive = !isActive(listing);
    setActionLoadingId(listingId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateListingStatus;
      if (!endpoint) {
        throw new Error("Update listing status endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({ listingId, isActive: nextIsActive }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update listing status.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      await fetchListings();
    } catch (e) {
      setError(e.message || "Failed to update listing status.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenStatusConfirm = (listing) => {
    setStatusTargetListing(listing);
    setOpenStatusConfirm(true);
  };

  const handleCloseStatusConfirm = () => {
    if (actionLoadingId !== null) return;
    setOpenStatusConfirm(false);
    setStatusTargetListing(null);
  };

  const handleConfirmToggleStatus = async () => {
    if (!statusTargetListing) return;
    await handleToggleListingStatus(statusTargetListing);
    setOpenStatusConfirm(false);
    setStatusTargetListing(null);
  };

  const handleOpenDeleteConfirm = (listing) => {
    setSelectedListing(listing);
    setOpenDeleteConfirm(true);
  };

  const handleOpenEditListing = (listing) => {
    const listingId = getListingId(listing);
    if (!listingId) {
      setError("Listing id is missing.");
      return;
    }

    setEditListingId(listingId);
    setEditListingTitle(getTitle(listing));
    setEditListingDescription(getDescription(listing));
    setEditListingPrice(String(getPrice(listing)));
    setEditListingQuantity(String(getQuantity(listing)));
    setEditExistingImageUrls(getListingImages(listing));
    setEditNewImageDataUrls([]);
    setOpenEditListing(true);
  };

  const handleCloseEditListing = () => {
    if (actionLoadingId !== null) return;
    setOpenEditListing(false);
    setEditListingId(0);
    setEditListingTitle("");
    setEditListingDescription("");
    setEditListingPrice("");
    setEditListingQuantity("");
    setEditExistingImageUrls([]);
    setEditNewImageDataUrls([]);
  };

  const convertFileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => reject(new Error(`Failed to read image file: ${file?.name || ""}`));
      reader.readAsDataURL(file);
    });

  const handleEditImagesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    try {
      const firstFile = files[0];
      const fileName = String(firstFile?.name || "").toLowerCase();
      const mimeType = String(firstFile?.type || "").toLowerCase();
      const isPngOrJpg =
        mimeType === "image/png" ||
        mimeType === "image/jpeg" ||
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg");

      if (!isPngOrJpg) {
        throw new Error("Only PNG and JPG images are allowed.");
      }

      const firstDataUrl = await convertFileToDataUrl(firstFile);
      if (!firstDataUrl) return;
      // Edit listing supports exactly one new image upload.
      setEditNewImageDataUrls([firstDataUrl]);
    } catch (e) {
      setError(e.message || "Failed to read selected images.");
    } finally {
      // allow selecting same files again
      // eslint-disable-next-line no-param-reassign
      event.target.value = "";
    }
  };

  const handleRemoveExistingEditImage = (indexToRemove) => {
    setEditExistingImageUrls((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleRemoveNewEditImage = (indexToRemove) => {
    setEditNewImageDataUrls((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const getImagePreviewSrc = (imageUrl) =>
    typeof imageUrl === "string" && imageUrl.startsWith("/")
      ? `${appSettings.baseUrl}${imageUrl}`
      : imageUrl;

  const handleCloseDeleteConfirm = () => {
    if (actionLoadingId !== null) return;
    setOpenDeleteConfirm(false);
    setSelectedListing(null);
  };

  const handleDeleteListing = async () => {
    const listingId = getListingId(selectedListing);
    if (!listingId) {
      setError("Listing id is missing.");
      return;
    }

    setActionLoadingId(listingId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.deleteListing;
      if (!endpoint) {
        throw new Error("Delete listing endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "DELETE",
        body: JSON.stringify({ listingId }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to delete listing.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseDeleteConfirm();
      await fetchListings();
    } catch (e) {
      setError(e.message || "Failed to delete listing.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSaveEditListing = async () => {
    const trimmedTitle = editListingTitle.trim();
    const trimmedDescription = editListingDescription.trim();
    const parsedPrice = Number(editListingPrice);
    const parsedQuantity = Number(editListingQuantity);

    if (!editListingId) {
      setError("Listing id is missing.");
      return;
    }
    if (!trimmedTitle) {
      setError("Listing title is required.");
      return;
    }
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setError("Listing price is required and must be a valid non-negative value.");
      return;
    }
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 0) {
      setError("A valid non-negative quantity is required.");
      return;
    }

    setActionLoadingId(editListingId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateListing;
      if (!endpoint) {
        throw new Error("Update listing endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          listingId: editListingId,
          title: trimmedTitle,
          description: trimmedDescription,
          price: parsedPrice,
          quantity: parsedQuantity,
          keptImageUrls: editExistingImageUrls,
          newImageDataUrls: editNewImageDataUrls,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update listing.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseEditListing();
      await fetchListings();
    } catch (e) {
      setError(e.message || "Failed to update listing.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.getAllListings;
      if (!endpoint) {
        throw new Error("Listings endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, { method: "GET", cache: "no-store" });

      if (!response.ok) {
        throw new Error("Failed to load listings.");
      }

      const data = await response.json();
      setListings(normalizeArray(data));
    } catch (e) {
      setError(e.message || "Failed to load listings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const tableData = useMemo(() => {
    const columns = [
      {
        Header: "",
        accessor: "expandText",
        align: "center",
        Cell: ({ row }) => row.original.expand,
      },
      {
        Header: "title",
        accessor: "titleSearch",
        align: "left",
        Cell: ({ row }) => row.original.title,
      },
      {
        Header: "category",
        accessor: "categorySearch",
        align: "left",
        Cell: ({ row }) => row.original.category,
      },
      {
        Header: "price",
        accessor: "priceValue",
        align: "right",
        Cell: ({ row }) => row.original.price,
      },
      {
        Header: "qty",
        accessor: "quantityValue",
        align: "center",
        Cell: ({ row }) => row.original.quantity,
      },
      {
        Header: "status",
        accessor: "statusText",
        align: "center",
        Cell: ({ row }) => row.original.status,
      },
      {
        Header: "listed",
        accessor: "listedText",
        align: "left",
        Cell: ({ row }) => row.original.listed,
      },
      {
        Header: "actions",
        accessor: "actionsText",
        align: "center",
        Cell: ({ row }) => row.original.actions,
      },
    ];

    const rows = listings.map((item, index) => {
      const rowKey = getRowKey(item, index);
      const isExpanded = !!expandedRows[rowKey];
      const description = getDescription(item);
      const titleLabel = getTitleLabel(item);
      const categoryLabel = getCategoryLabel(item);
      const priceValue = getPrice(item);
      const quantityValue = getQuantity(item);
      const statusText = isActive(item) ? "active" : "inactive";
      const listedText = formatDateForUser(getCreatedDate(item));
      const listingId = getListingId(item);

      return {
        expandText: isExpanded ? "collapse" : "expand",
        expand: (
          <Tooltip title={isExpanded ? "Collapse row" : "Expand row"}>
            <span>
              <IconButton size="small" color="info" onClick={() => toggleRowExpanded(rowKey)}>
                <Icon fontSize="small">{isExpanded ? "expand_less" : "expand_more"}</Icon>
              </IconButton>
            </span>
          </Tooltip>
        ),
        titleSearch: `${titleLabel} ${description}`.trim(),
        title: (
          <MDBox>
            <MDTypography variant="button" color="text" fontWeight="medium">
              {titleLabel}
            </MDTypography>
            {isExpanded && (
              <MDBox
                mt={1}
                px={1.25}
                py={1}
                borderRadius="md"
                sx={{ backgroundColor: "rgba(25, 118, 210, 0.08)" }}
              >
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Description
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  {description || "-"}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        ),
        categorySearch: categoryLabel,
        category: (
          <MDTypography variant="caption" color="text">
            {categoryLabel}
          </MDTypography>
        ),
        priceValue,
        price: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {Number(priceValue).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </MDTypography>
        ),
        quantityValue,
        quantity: (
          <MDTypography variant="caption" color="text">
            {quantityValue}
          </MDTypography>
        ),
        statusText,
        status: (
          <MDBadge
            badgeContent={statusText}
            color={isActive(item) ? "success" : "secondary"}
            variant="gradient"
            size="sm"
          />
        ),
        listedText,
        listed: (
          <MDTypography variant="caption" color="text">
            {listedText}
          </MDTypography>
        ),
        actionsText: `ID: ${listingId || "-"}`,
        actions: (
          <MDBox display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Tooltip title="Edit Listing">
              <span>
                <IconButton
                  color="info"
                  size="small"
                  onClick={() => handleOpenEditListing(item)}
                  disabled={actionLoadingId === listingId}
                >
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isActive(item) ? "Deactivate Listing" : "Activate Listing"}>
              <span>
                <IconButton
                  color={isActive(item) ? "warning" : "success"}
                  size="small"
                  onClick={() => handleOpenStatusConfirm(item)}
                  disabled={actionLoadingId === listingId}
                >
                  <Icon fontSize="small">{isActive(item) ? "toggle_off" : "toggle_on"}</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Listing">
              <span>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleOpenDeleteConfirm(item)}
                  disabled={actionLoadingId === listingId}
                >
                  <Icon fontSize="small">delete</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </MDBox>
        ),
      };
    });

    return { columns, rows };
  }, [listings, actionLoadingId, expandedRows]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Listings
                </MDTypography>
                <MDButton variant="gradient" color="info" onClick={fetchListings}>
                  <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>
                  &nbsp;refresh
                </MDButton>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader size={42} />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        mx={2}
                        mb={2}
                        py={1}
                        px={2}
                        variant="gradient"
                        sx={{ backgroundColor: "lightcoral" }}
                        borderRadius="lg"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {error}
                        </MDTypography>
                        <MDTypography
                          color="white"
                          sx={{ cursor: "pointer", ml: 2, lineHeight: 0 }}
                          onClick={() => setError(null)}
                        >
                          x
                        </MDTypography>
                      </MDBox>
                    )}

                    <DataTable
                      table={tableData}
                      isSorted
                      canSearch
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                      showTotalEntries
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog open={openDeleteConfirm} onClose={handleCloseDeleteConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Delete Listing</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Delete <strong>{getTitle(selectedListing) || "this listing"}</strong>? This action
            cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseDeleteConfirm}
            color="secondary"
            disabled={actionLoadingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleDeleteListing}
            variant="gradient"
            color="error"
            disabled={actionLoadingId !== null}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openStatusConfirm} onClose={handleCloseStatusConfirm} fullWidth maxWidth="xs">
        <DialogTitle>
          {isActive(statusTargetListing) ? "Deactivate Listing" : "Activate Listing"}
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to {isActive(statusTargetListing) ? "deactivate" : "activate"}{" "}
            <strong>{getTitle(statusTargetListing) || "this listing"}</strong>?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseStatusConfirm}
            color="secondary"
            disabled={actionLoadingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmToggleStatus}
            variant="gradient"
            color={isActive(statusTargetListing) ? "warning" : "success"}
            disabled={actionLoadingId !== null}
          >
            {isActive(statusTargetListing) ? "Deactivate" : "Activate"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openEditListing} onClose={handleCloseEditListing} fullWidth maxWidth="sm">
        <DialogTitle>Edit Listing</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <TextField
              autoFocus
              fullWidth
              label="Title"
              required
              value={editListingTitle}
              onChange={(e) => setEditListingTitle(e.target.value)}
            />
            <TextField
              fullWidth
              label="Description"
              value={editListingDescription}
              onChange={(e) => setEditListingDescription(e.target.value)}
              multiline
              minRows={3}
            />
            <TextField
              fullWidth
              label="Price"
              type="number"
              required
              inputProps={{ min: 0, step: "0.01" }}
              value={editListingPrice}
              onChange={(e) => setEditListingPrice(e.target.value)}
            />
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              required
              inputProps={{ min: 0, step: "1" }}
              value={editListingQuantity}
              onChange={(e) => setEditListingQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && actionLoadingId === null) {
                  handleSaveEditListing();
                }
              }}
            />
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Existing Images
              </MDTypography>
              {editExistingImageUrls.length === 0 ? (
                <MDTypography variant="caption" color="text">
                  No images.
                </MDTypography>
              ) : (
                <MDBox display="grid" gap={1}>
                  {editExistingImageUrls.map((imageUrl, index) => (
                    <MDBox
                      key={`existing-edit-image-${index}`}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      px={1}
                      py={0.75}
                      borderRadius="md"
                      sx={{ backgroundColor: "rgba(0,0,0,0.04)" }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDBox
                          component="img"
                          src={getImagePreviewSrc(imageUrl)}
                          alt={`existing-listing-image-${index + 1}`}
                          sx={{ width: 36, height: 36, borderRadius: "6px", objectFit: "cover" }}
                        />
                        <MDTypography variant="caption" color="text">
                          Image {index + 1}
                        </MDTypography>
                      </MDBox>
                      <MDButton
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleRemoveExistingEditImage(index)}
                      >
                        Remove
                      </MDButton>
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Add New Images
              </MDTypography>
              <MDBox
                px={1.25}
                py={1.25}
                borderRadius="md"
                sx={{ border: "1px dashed rgba(0,0,0,0.24)", backgroundColor: "rgba(0,0,0,0.01)" }}
              >
                <input
                  id="listing-image-upload-edit"
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleEditImagesSelected}
                  style={{ display: "none" }}
                />
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <MDButton
                    component="label"
                    htmlFor="listing-image-upload-edit"
                    variant="outlined"
                    color="info"
                    size="small"
                  >
                    {editNewImageDataUrls.length > 0 ? "Replace Image" : "Upload Image"}
                  </MDButton>
                  <MDTypography variant="caption" color="text">
                    {editNewImageDataUrls.length > 0
                      ? "1 image selected"
                      : "PNG or JPG only. Only one image is allowed."}
                  </MDTypography>
                </MDBox>
              </MDBox>
              {editNewImageDataUrls.length > 0 && (
                <MDBox mt={1} display="grid" gap={1}>
                  {editNewImageDataUrls.map((imageSrc, index) => (
                    <MDBox
                      key={`new-edit-image-${index}`}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      px={1}
                      py={0.75}
                      borderRadius="md"
                      sx={{ backgroundColor: "rgba(0,0,0,0.04)" }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDBox
                          component="img"
                          src={imageSrc}
                          alt={`new-listing-image-${index + 1}`}
                          sx={{ width: 36, height: 36, borderRadius: "6px", objectFit: "cover" }}
                        />
                        <MDTypography variant="caption" color="text">
                          New image {index + 1}
                        </MDTypography>
                      </MDBox>
                      <MDButton
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleRemoveNewEditImage(index)}
                      >
                        Remove
                      </MDButton>
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseEditListing}
            color="secondary"
            disabled={actionLoadingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveEditListing}
            variant="gradient"
            color="info"
            disabled={actionLoadingId !== null}
          >
            Update
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Listings;
